import { useRef } from 'react';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';

export type ConfirmTone = 'default' | 'danger' | 'success';

interface ConfirmPopUpProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  isProcessing?: boolean;
  /** When true, only the confirm button is shown. */
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const TONE_STYLES: Record<ConfirmTone, { button: string; iconWrap: string; icon: string }> = {
  default: {
    button: 'bg-emerald-800 hover:bg-emerald-700 shadow-emerald-200',
    iconWrap: 'bg-emerald-100',
    icon: 'text-emerald-700',
  },
  danger: {
    button: 'bg-red-600 hover:bg-red-700 shadow-red-200',
    iconWrap: 'bg-red-100',
    icon: 'text-red-600',
  },
  success: {
    button: 'bg-emerald-800 hover:bg-emerald-700 shadow-emerald-200',
    iconWrap: 'bg-emerald-100',
    icon: 'text-emerald-700',
  },
};

export default function ConfirmPopUp({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  isProcessing = false,
  hideCancel = false,
  onConfirm,
  onCancel,
}: ConfirmPopUpProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, {
    enabled: isOpen,
    onEscape: () => {
      if (!isProcessing) onCancel();
    },
  });

  if (!isOpen) return null;

  const styles = TONE_STYLES[tone];
  const Icon = tone === 'success' ? CheckCircle : AlertTriangle;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => !isProcessing && onCancel()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex items-start gap-4">
          <div className={`p-3 rounded-full shrink-0 ${styles.iconWrap}`}>
            <Icon className={`w-6 h-6 ${styles.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          {!hideCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg flex items-center transition-all disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
          >
            {isProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
